import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider
const Tooltip = TooltipPrimitive.Root

/**
 * `:focus-visible` responde à pergunta certa: o browser deve DESENHAR o foco?
 *
 * Ele vale quando o foco veio do teclado (Tab, atalho) e não quando veio de
 * clique ou de um `.focus()` programático depois de um clique — que é
 * exatamente a distinção que o tooltip precisa fazer.
 *
 * Browser sem suporte cai no comportamento antigo (abrir): esconder o tooltip
 * de quem navega por teclado seria trocar um incômodo visual por uma perda de
 * acessibilidade.
 */
function ehFocoDeTeclado(elemento: Element): boolean {
  try {
    return elemento.matches(":focus-visible")
  } catch {
    return true
  }
}

/**
 * Trigger que NÃO abre o tooltip em foco programático.
 *
 * Ao abrir um Dialog, o `FocusScope` do Radix move o foco para o primeiro
 * elemento tabbable de dentro dele. Se esse elemento for um trigger de tooltip,
 * o Radix Tooltip lê o foco como intenção do usuário e abre a bolha sozinho — o
 * modal nasce com um balão de ajuda aberto, apontando para um botão que ninguém
 * tocou. Vale para qualquer `.focus()` nosso, não só o do Dialog.
 *
 * O ponto de intercepção é o `onFocus`: o Radix o compõe com
 * `composeEventHandlers(props.onFocus, ...)`, que só chama o handler interno se
 * o evento não teve `preventDefault()`. Prevenir aqui cancela a abertura sem
 * mexer em mais nada do comportamento (hover, teclado, fechar no clique).
 *
 * A correção vale para o app inteiro porque ninguém importa
 * `@radix-ui/react-tooltip` diretamente — todos os triggers passam por aqui.
 */
const TooltipTrigger = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onFocus, ...props }, ref) => (
  <TooltipPrimitive.Trigger
    ref={ref}
    {...props}
    onFocus={(event) => {
      onFocus?.(event)
      if (event.defaultPrevented) return
      if (!ehFocoDeTeclado(event.currentTarget)) event.preventDefault()
    }}
  />
))
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-[1500] overflow-hidden rounded-md border border-[#3A332C] bg-[#2C2620] px-3 py-1.5 text-sm text-[#EDE8E1] shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
